import { APPLICATIONS, trierApplications } from "@repo/utils";
import type { Id } from "../../../convex/_generated/dataModel";
import type { ScorecardOption } from "./scorecardsCampagne";
import {
  ApplicationEnum,
  TemplateInbasketEnum,
  TypeEvalEnum,
} from "../../../convex/schema";

/**
 * Ce que le formulaire de création retient de la composition : les
 * applications cochées et, pour celles qui en demandent un, le choix fait
 * à la création (variante Inbasket, scorecard + version de l'entretien).
 */
export interface ChoixComposition {
  cochees: ApplicationEnum[];
  varianteInbasket: TemplateInbasketEnum;
  /** Entretien structuré : slug de la scorecard de l'org et version retenue. */
  entretien?: { slug: string; versionId: Id<"scorecardVersions"> };
}

export const CHOIX_INITIAL: ChoixComposition = {
  cochees: [],
  varianteInbasket: TemplateInbasketEnum.DEFAULT,
};

export const LIBELLES_VARIANTE_INBASKET: Record<TemplateInbasketEnum, string> = {
  [TemplateInbasketEnum.DEFAULT]: "Complète",
  [TemplateInbasketEnum.RAPIDE]: "Rapide",
};

export interface EvaluationComposee {
  typeEval: TypeEvalEnum;
  template: string;
  scorecardVersionId?: Id<"scorecardVersions">;
}

/** Ce qui manque pour créer la campagne ; `null` si la composition est complète. */
export function erreurComposition(choix: ChoixComposition): string | null {
  if (choix.cochees.length === 0) return "Choisissez au moins une épreuve.";
  if (choix.cochees.includes(ApplicationEnum.ENT_STRUCT) && !choix.entretien) {
    return "Choisissez la scorecard de l'entretien FairView.";
  }
  return null;
}

/**
 * `evaluationsConfig` envoyée au serveur, dans l'ordre du catalogue (qui est
 * l'ordre de passage). Suppose `erreurComposition(choix) === null`.
 */
export function evaluationsDeComposition(
  choix: ChoixComposition,
): EvaluationComposee[] {
  return trierApplications(choix.cochees).flatMap((cle): EvaluationComposee[] => {
    const application = APPLICATIONS.find((a) => a.cle === cle);
    if (!application) return [];
    switch (cle) {
      case ApplicationEnum.INBASKET:
        return [{ typeEval: application.typeEval, template: choix.varianteInbasket }];
      case ApplicationEnum.ENT_STRUCT:
        return choix.entretien
          ? [
              {
                typeEval: application.typeEval,
                template: choix.entretien.slug,
                scorecardVersionId: choix.entretien.versionId,
              },
            ]
          : [];
      default:
        return application.template
          ? [{ typeEval: application.typeEval, template: application.template }]
          : [];
    }
  });
}

/**
 * Coche ou décoche une application. Décocher l'entretien oublie sa scorecard ;
 * le cocher dans une organisation qui n'en a qu'une présélectionne sa version
 * en service (la première, les versions allant de la plus récente à la plus
 * ancienne). Avec plusieurs scorecards, le choix revient au recruteur.
 */
export function basculerApplication(
  choix: ChoixComposition,
  cle: ApplicationEnum,
  cochee: boolean,
  scorecards?: ScorecardOption[],
): ChoixComposition {
  const cochees = cochee
    ? trierApplications([...choix.cochees, cle])
    : choix.cochees.filter((c) => c !== cle);
  if (cle !== ApplicationEnum.ENT_STRUCT) return { ...choix, cochees };
  if (!cochee) return { ...choix, cochees, entretien: undefined };
  const seule = scorecards?.length === 1 ? scorecards[0] : undefined;
  const enService = seule?.versions[0];
  return {
    ...choix,
    cochees,
    entretien:
      choix.entretien ??
      (seule && enService
        ? { slug: seule.slug, versionId: enService._id }
        : undefined),
  };
}
